import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { AcceptedFormats } from './FileUpload';

type UploadStatus = 'idle' | 'uploading' | 'error';

type FileWithPreview = { file: File; preview?: string; isImage: boolean; id: string };

interface UseFileUploadOptions {
  maxSizeMB?: number;
  acceptedFormats: AcceptedFormats;
  onFilesChange?: (files: File[]) => void;
  controlledFiles?: File[];
  invalidFileNames?: string[];
}

const isImageFile = (file: File): boolean => {
  // Check if the file is an image type
  return file.type.startsWith('image/');
};

const generateFileId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export const acceptedExtensionsOf = (formats: AcceptedFormats): string[] => [
  ...new Set(Object.values(formats).flat()),
];

export const useFileUpload = ({
  maxSizeMB = 10,
  acceptedFormats,
  onFilesChange,
  controlledFiles,
  invalidFileNames,
}: UseFileUploadOptions) => {
  const { t } = useTranslation();
  const isControlled = controlledFiles !== undefined;
  const [filesWithPreviews, setFilesWithPreviews] = useState<FileWithPreview[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const previousControlledFilesRef = useRef<File[]>(null);

  const acceptedTypes = useCallback(() => {
    return Object.keys(acceptedFormats);
  }, [acceptedFormats]);

  const acceptedExtensions = useCallback(() => {
    return acceptedExtensionsOf(acceptedFormats);
  }, [acceptedFormats]);

  const validateFiles = useCallback(
    (files: File[], existingFiles: File[] = []): File[] => {
      if (!files || files.length === 0) {
        return [];
      }

      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      const validFiles: File[] = [];
      let errorMessage = null;
      const types = acceptedTypes();
      const extensions = acceptedExtensions();

      // Create a set of existing file names for quick lookup
      const existingFileNames = new Set(existingFiles.map((file) => file.name));

      // First pass: check for duplicates within the current drop
      const duplicateInDrop = files.find((file, index) =>
        files.slice(0, index).some((prevFile) => prevFile.name === file.name)
      );

      if (duplicateInDrop) {
        setError(t('file_upload.file_duplicate_name', { 0: duplicateInDrop.name }));
        return [];
      }

      files.forEach((file) => {
        if (!types.includes(file.type)) {
          errorMessage = t('file_upload.file_type_not_supported', { 0: extensions.join(', ') });
          return;
        }

        if (file.size > maxSizeBytes) {
          errorMessage = t('file_upload.file_size_exceeds_limit', { 0: maxSizeMB });
          return;
        }

        // Check for duplicate file names within the current upload session
        if (existingFileNames.has(file.name)) {
          errorMessage = t('file_upload.file_duplicate_name', { 0: file.name });
          return;
        }

        const nameWithoutExtension = file.name.split('.').slice(0, -1).join('.');
        if (invalidFileNames?.includes(nameWithoutExtension)) {
          errorMessage = t('file_upload.file_duplicate_name', { 0: file.name });
          return;
        }

        validFiles.push(file);
      });

      setError(errorMessage);
      return validFiles;
    },
    [maxSizeMB, acceptedTypes, acceptedExtensions, t, invalidFileNames]
  );

  const createPreviews = useCallback((files: File[]): FileWithPreview[] => {
    return files.map((file) => {
      const isImage = isImageFile(file);
      return {
        file,
        preview: isImage ? URL.createObjectURL(file) : undefined,
        isImage,
        id: generateFileId(),
      };
    });
  }, []);

  const handleFiles = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[] = []) => {
      // Get existing files for duplicate checking
      const existingFiles = isControlled
        ? controlledFiles || []
        : filesWithPreviews.map((f) => f.file);
      const validFiles = validateFiles(acceptedFiles, existingFiles);

      if (rejectedFiles.length > 0) {
        if (rejectedFiles[0].errors[0]?.code === 'too-many-files') {
          setError(t('file_upload.too_many_files'));
          return;
        }
      }
      if (validFiles.length > 0) {
        if (isControlled) {
          // In controlled mode, just notify parent of new files
          if (onFilesChange) {
            const currentFiles = controlledFiles || [];
            // Defer the callback to avoid state updates during render
            setTimeout(() => {
              onFilesChange([...currentFiles, ...validFiles]);
            }, 0);
          }
        } else {
          // In uncontrolled mode, manage state internally
          const newFilesWithPreviews = createPreviews(validFiles);
          setFilesWithPreviews((prev) => {
            const updated = [...prev, ...newFilesWithPreviews];
            // Defer the callback to avoid state updates during render
            if (onFilesChange) {
              setTimeout(() => {
                onFilesChange(updated.map((f) => f.file));
              }, 0);
            }
            return updated;
          });
        }
        setError(null);
      }
    },
    [
      validateFiles,
      t,
      isControlled,
      onFilesChange,
      controlledFiles,
      createPreviews,
      filesWithPreviews,
    ]
  );

  const removeFile = useCallback(
    (fileId: string) => {
      if (isControlled) {
        // In controlled mode, find the file to remove and notify parent
        setFilesWithPreviews((prevFiles) => {
          const fileWithPreview = prevFiles.find((f) => f.id === fileId);
          if (fileWithPreview && onFilesChange) {
            const updatedFiles = (controlledFiles || []).filter(
              (f) => f.name !== fileWithPreview.file.name
            );
            // Clear error if no files remain
            if (updatedFiles.length === 0) {
              setError(null);
            }
            // Defer the callback to avoid state updates during render
            setTimeout(() => {
              onFilesChange(updatedFiles);
            }, 0);
          }
          return prevFiles; // Don't change state in controlled mode
        });
      } else {
        // In uncontrolled mode, manage state internally
        setFilesWithPreviews((prev) => {
          const fileToRemove = prev.find((f) => f.id === fileId);
          if (fileToRemove?.preview) {
            URL.revokeObjectURL(fileToRemove.preview);
          }
          const updated = prev.filter((f) => f.id !== fileId);
          // Clear error if no files remain
          if (updated.length === 0) {
            setError(null);
          }
          if (onFilesChange) {
            // Defer the callback to avoid state updates during render
            setTimeout(() => {
              onFilesChange(updated.map((f) => f.file));
            }, 0);
          }
          return updated;
        });
      }
    },
    [onFilesChange, isControlled, controlledFiles]
  );

  const startUpload = useCallback(() => {
    setUploadStatus('uploading');
  }, []);

  const clearFiles = useCallback(() => {
    setFilesWithPreviews((prevFiles) => {
      prevFiles.forEach((fileWithPreview) => {
        if (fileWithPreview.preview) {
          URL.revokeObjectURL(fileWithPreview.preview);
        }
      });

      if (isControlled) {
        if (onFilesChange) {
          // Defer the callback to avoid state updates during render
          setTimeout(() => {
            onFilesChange([]);
          }, 0);
        }
        return prevFiles; // Don't change state in controlled mode, parent will handle it
      } else {
        return []; // Clear files in uncontrolled mode
      }
    });
    setUploadStatus('idle');
    setError(null);
  }, [onFilesChange, isControlled]);

  const completeUpload = useCallback(() => {
    clearFiles();
  }, [clearFiles]);

  const failUpload = useCallback((errorMessage: string) => {
    setUploadStatus('error');
    setError(errorMessage);
  }, []);

  // Sync controlled files with internal state
  useEffect(() => {
    if (isControlled && controlledFiles) {
      // Check if controlled files have actually changed
      const previousFiles = previousControlledFilesRef.current;
      const filesChanged =
        !previousFiles ||
        previousFiles.length !== controlledFiles.length ||
        previousFiles.some((file, index) => file !== controlledFiles[index]);

      if (filesChanged) {
        // Clean up old previews
        setFilesWithPreviews((prevFiles) => {
          prevFiles.forEach((fileWithPreview) => {
            if (fileWithPreview.preview) {
              URL.revokeObjectURL(fileWithPreview.preview);
            }
          });

          // Create new previews for controlled files
          const newFilesWithPreviews = createPreviews(controlledFiles);
          return newFilesWithPreviews;
        });

        // Update the ref with current controlled files
        previousControlledFilesRef.current = controlledFiles;
      }
    }
  }, [controlledFiles, isControlled, createPreviews]);

  useEffect(() => {
    return () => {
      filesWithPreviews.forEach((fileWithPreview) => {
        if (fileWithPreview.preview) {
          URL.revokeObjectURL(fileWithPreview.preview);
        }
      });
    };
  }, [filesWithPreviews]);

  const memoizedAcceptedExtensions = useMemo(() => acceptedExtensions(), [acceptedExtensions]);

  return {
    filesWithPreviews,
    files: filesWithPreviews.map((f) => f.file),
    error,
    uploadStatus,
    acceptedExtensions: memoizedAcceptedExtensions,
    handleFiles,
    removeFile,
    startUpload,
    completeUpload,
    failUpload,
    clearFiles,
  };
};
